import { z } from 'zod';
import type { ChatContextType } from '../../enums/chat.js';
import { ChatResponseType } from '../../enums/chat.js';
import type { ChatContextItem } from '../../types/agents.js';
import { chartPreferencesTypeEnum, chartRecordSchema } from '../../types/chart-preferences.js';
import type { ChatAction } from '../../types/chat.actions.js';
import type { Chart } from '../../types/chat.chart.js';
import type { CustomFilter } from '../../types/chat.filters.js';
import type { DisplayMode } from '../../types/chat.js';
import type {
  Chat,
  BrandInsightsInstanceContext,
  ChatHistory,
  WorkspaceFolder,
  WorkspaceObject,
  InputAttachment,
} from '../../types/index.js';
import type { ChatMention } from '../../types/mentions.js';
import type { ChatQuestion } from '../../types/questions.js';

export declare namespace SubmitChatQuery {
  export type Request = {
    chatIdentifier: string;
    identifier: string;
    parentQueryIdentifier: string | null;
    query: string;
    context: BrandInsightsInstanceContext.ItemWithMetadata;
    filters: Chat.Filters;
    action: ChatAction | null;
    brandIdentifier: string;
    promptIdentifier?: string;
    displayMode?: DisplayMode;
    applicationIdentifier: string;
    /** Subject mentions extracted from @ tags in the chat input */
    mentions?: ChatMention[];
    /** Files attached by the user, uploaded to S3 before query submission */
    inputAttachments?: InputAttachment[];
  };

  export type Response = {
    identifier: string;
  };
}

export declare namespace RegenerateQuery {
  export type Request = {
    chatIdentifier: string;
    identifier: string;
    parentQueryIdentifier: string | null;
    query: string;
    context: BrandInsightsInstanceContext.ItemWithMetadata;
    filters: Chat.Filters;
    brandIdentifier: string;
  };

  export type Response = {
    identifier: string;
  };
}

export declare namespace GetChatHistory {
  export type Request = {
    mode: ChatHistory.Mode;
    cursor: number;
    pageSize: number;
    context: BrandInsightsInstanceContext.ItemWithMetadata;
    includeAdditional?: string[];
    brandIdentifier: string;
    isBookmarked?: boolean;
    name?: string;
  };

  export type Response<D = string> = {
    hasMore: boolean;
    items: Chat.Instance<D>[];
    shared: Chat.Instance<D>[];
  };
}

export declare namespace SearchChatHistory {
  export type Request = {
    mode: ChatHistory.Mode;
    cursor: number;
    pageSize: number;
    context: BrandInsightsInstanceContext.ItemWithMetadata;
    includeAdditional?: string[];
    brandIdentifier: string;
    name: string;
    isBookmarked?: boolean;
  };

  export type Response<D = string> = {
    hasMore: boolean;
    items: Chat.Instance<D>[];
    shared: Chat.Instance<D>[];
  };
}

export declare namespace GetChatInitialMessage {
  export type Request = {
    context: BrandInsightsInstanceContext.ItemWithMetadata;
    applicationIdentifier: string;
    brandIdentifier: string;
  };

  export type Response = Chat.QueryResponse<ChatResponseType>;
}

export declare namespace FileUploadPreCheck {
  type PreCheckFile = {
    identifier: string;
    name: string;
  };

  export type Request = {
    chatInstanceIdentifier: string;
    files: PreCheckFile[];
    parentObjectId: number;
  };
  export type Response = {
    existing: string[];
    folder: WorkspaceFolder;
    object: WorkspaceObject;
  };
}

export declare namespace UploadFiles {
  export type Request = {
    chatInstanceIdentifier: string;
    context: BrandInsightsInstanceContext.ItemWithMetadata;
    files: {
      identifier: string;
      name: string;
    }[];
    identifier: string;
    brandIdentifier: string;
  };

  export type Response<T = Date> = {
    chatInstance: Chat.Instance<T>;
    fileUpload: Chat.FileUpload<T>;
  };
}

export declare namespace LookupContext {
  export type Request = {
    entityId: number;
    objectId: number;
    contextType: ChatContextType;
  };

  export type Response = {
    context: BrandInsightsInstanceContext.ItemWithMetadata;
  };
}

export declare namespace GetChatContextAccess {
  export type Request = {
    context: BrandInsightsInstanceContext.ItemWithMetadata;
  };

  export type Response = {
    value: boolean;
  };
}

export declare namespace SearchChatFilters {
  export type SearchField =
    | 'product'
    | 'therapeutic-sub-area'
    | 'interest-area'
    | 'therapeutic-area'
    | 'hcps'
    | 'insight-type'
    | 'interaction-sub-type'
    | 'interaction-detail'
    | 'call-center-source'
    | 'call-center-sub-source';

  export type Request = {
    excluded: (number | string)[];
    type: SearchField;
    value: string;
    brandIdentifier: string;
  };

  export type Response = {
    results: CustomFilter.SingleSelectFilter[];
  };
}

export declare namespace GetPendingQuery {
  export type Request = {
    queryIdentifier: string;
  };

  export type Response = {
    chatInstance: Chat.Instance<string>;
    query: Chat.Query<ChatResponseType, string>;
  };
}

export declare namespace ContextSearch {
  export type Request = {
    q: string;
    type: ChatContextType;
    brandIdentifier: string;
    contextIdentifier: string;
  };

  export type Response = {
    results: {
      entityId: number;
      objectId: number;
      name: string;
      type: ChatContextType;
    }[];
  };
}

export declare namespace SearchTags {
  export type Request = {
    context: BrandInsightsInstanceContext.ItemWithMetadata;
    query: string;
  };

  export type Response = {
    total: number;
    results: Array<{
      id: number;
      name: string;
      metadata?: {
        canRemove?: boolean;
        segmentIdentifier?: string | null;
      };
    }>;
  };
}

export declare namespace SubmitChatAction {
  export type Request = {
    action: ChatAction;
    queryId: number;
  };

  export type Response = void;
}

export declare namespace SendHcpSchedulingEmail {
  export type Request = {
    queryId: number;
    event: Chat.QueryState.SchedulingAssistant['event'];
  };
  export type Response = {
    query: Chat.Query<ChatResponseType, string>;
  };
}

export declare namespace ExportChartImage {
  export type Request = {
    chartData: Chart;
    title: string;
    colors: string[];
  };

  export type Response = {
    websocketKey: string;
  };
}

export declare namespace ExportChartPptx {
  export type Request = {
    chartData: Chart;
    title: string;
    colors: string[];
  };

  export type Response = {
    websocketKey: string;
  };
}

export declare namespace ExportChartCsv {
  export type Request = {
    quantItemId: number;
  };
  export type Response = {
    websocketKey: string;
  };
}

export declare namespace GetChatContexts {
  export type Request = {
    brandIdentifier: string;
    applicationIdentifier?: string;
  };

  export type Response = {
    items: ChatContextItem[];
  };
}

export declare namespace GetChatQuestions {
  export type Request = {
    brandIdentifier: string;
  };

  export type Response = {
    questions: ChatQuestion[];
  };
}
export declare namespace GetChatLandingCards {
  export type Request = {
    brandIdentifier: string;
    applicationIdentifier: string;
  };

  export type Response = {
    cards: {
      id: number;
      title: string;
      description: string;
      icon: string;
      chatContext: {
        id: number;
        name: string;
        identifier: string;
      };
      actionData: unknown | null;
      actionType: number;
      isHidden: boolean;
      order: number;
    }[];
    prompts: {
      name: string;
      description: string;
      promptIdentifier: string | null;
    }[];
  };
}

export declare namespace SearchChatProjects {
  export type Request = {
    context: BrandInsightsInstanceContext.ItemWithMetadata;
    query: string;
  };
  export type Response = {
    empty: boolean;
    results: {
      id: number;
      name: string;
    }[];
  };
}

export const createChartRecordRequestBodySchema = z.object({
  initialChartType: chartPreferencesTypeEnum,
});
export const createChartRecordResponseBodySchema = z.object({
  record: chartRecordSchema,
});

export const updateChartRecordOverrideParamsSchema = z.object({
  recordId: z.coerce.number(),
});
export const updateChartRecordOverrideRequestBodySchema = z.object({
  overrideChartTypes: z.array(chartPreferencesTypeEnum),
});
export const updateChartRecordOverrideResponseBodySchema = z.object({
  record: chartRecordSchema,
});

export declare namespace CreateChartRecord {
  export type Request = z.infer<typeof createChartRecordRequestBodySchema>;
  export type Response = z.infer<typeof createChartRecordResponseBodySchema>;
}

export declare namespace UpdateChartRecordOverride {
  export type Request = z.infer<typeof updateChartRecordOverrideRequestBodySchema> &
    z.infer<typeof updateChartRecordOverrideParamsSchema>;
  export type Response = z.infer<typeof updateChartRecordOverrideResponseBodySchema>;
}
